import { useState } from "react";
import useTaskContext from "./useTaskContext";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { useSnackbar } from "@sagecentral/core-ui";
import { ValidationSummary } from "components/shared";

type FieldUpdater = (newValue: any, taskIdOverride?: number) => Promise<boolean>;
type PostUpdateSignature = (data: any) => void;
type UpdateFailedSignature = (error: any) => void;
type UseTaskFieldUpdaterResult = [
    FieldUpdater,
    boolean,
    string
];
type UseTaskFieldUpdaterSignature = (
    field: string,
    postUpdate?: PostUpdateSignature,
    updateFailed?: UpdateFailedSignature
) => UseTaskFieldUpdaterResult;

const useTaskFieldUpdater: UseTaskFieldUpdaterSignature = (field, postUpdate, updateFailed) => {
    const taskContext = useTaskContext();
    const { task } = taskContext;
    const { displaySnackbar } = useSnackbar();     
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const [working, setWorking] = useState<boolean>(false);
    const [error] = useState<string>('');

    const fieldUpdater = async (newValue: string, taskIdOverride?: number) => {
        try {
            //blanking is not allowed for any of the fields
            if (!newValue || newValue.toString().trim() === '') {
                return false;
             }

            if ((task || taskIdOverride) && user && user.access_token) {
                setWorking(true);
                const resp = await fetcher(`/api/v3/tasks/${task?.id ?? taskIdOverride}/${field}`, {
                    method: 'PATCH',
                    body: JSON.stringify(newValue)
                }, async r => {
                    if (r.status === 400) {
                        const data = await r.json();
                        displaySnackbar('warning', <ValidationSummary validationDetail={data} />);
                    } else {
                        return false;
                    }
                    return true;
                });

                if (!resp.ok) {
                    if (resp.status === 400) {
                        return false;
                    }
                }

                const result = await resp.json();
                if (postUpdate) {
                    postUpdate(result);
                }
                return true;

            } else {
                return false;
            }
        } catch (err) {
            return false;
        } finally {
            setWorking(false);
        }
    };


    return [
        fieldUpdater,
        working,
        error
    ];
};

export default useTaskFieldUpdater;